import { Fragment, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

export interface AvailablePermission {
  code: string;
  module: string;
  action: string;
  displayName: string;
}

interface PermissionMatrixProps {
  availablePermissions: AvailablePermission[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}

const MODULE_LABELS: Record<string, string> = {
  catalog: "Catalog",
  profile: "Profile",
  control_mapping: "Control Mapping",
  component: "Component Definition",
  ssp: "SSP",
  assessment_plan: "Assessment Plan",
  assessment_execution: "Assessment Execution",
  assessment_review: "Assessment Review",
  assessment_result: "Assessment Result",
  poam: "POA&M",
  document: "Governance Document",
  evidence: "Supporting Evidence",
  user: "User",
  role: "Role",
  role_mapping: "Role Mapping",
  organization: "Organization",
  cmdb: "CMDB",
  validation_rule: "Validation Rule",
  llm_configuration: "LLM Configuration",
  dashboard_configuration: "Dashboard Configuration",
  report_configuration: "Report Configuration",
};

const ACTION_ORDER = [
  "create",
  "view",
  "update",
  "delete",
  "export",
  "approve",
  "publish",
];
const ACTION_LABELS: Record<string, string> = {
  create: "Create",
  view: "View",
  update: "Update",
  delete: "Delete",
  export: "Export",
  approve: "Approve",
  publish: "Publish",
  execute: "Execute",
};

// Module permissions configuration: which modules support which actions
const MODULE_PERMISSIONS_CONFIG: Record<string, Set<string>> = {
  // Full permissions
  document: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  evidence: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  catalog: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  profile: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  control_mapping: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  component: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),
  ssp: new Set([
    "create",
    "view",
    "update",
    "delete",
    "export",
    "approve",
    "publish",
  ]),

  // Assessment modules - no create, delete, export, approve, publish
  assessment_plan: new Set(["create", "view", "update", "approve"]),
  assessment_execution: new Set(["view", "update"]),
  assessment_review: new Set(["view", "update"]),
  assessment_result: new Set(["view"]),

  // Risk
  poam: new Set(["view", "update", "approve"]),

  // Administration
  organization: new Set(["create", "view", "update", "delete"]),
  user: new Set(["view", "update"]),
  role: new Set(["create", "view", "update", "delete"]),
  role_mapping: new Set(["view", "update"]),
  cmdb: new Set(["create", "view", "update", "delete"]),
  validation_rule: new Set(["create", "view", "update", "delete"]),
  llm_configuration: new Set(["create", "view", "update", "delete"]),

  // Insights - dashboard configuration is view-only
  dashboard_configuration: new Set(["view"]),
};

interface ModuleGroup {
  key: string;
  label: string;
  modules: string[];
}

const MODULE_GROUPS: ModuleGroup[] = [
  {
    key: "governance",
    label: "Governance",
    modules: ["document", "evidence", "catalog", "profile", "control_mapping"],
  },
  { key: "system", label: "System", modules: ["component", "ssp"] },
  {
    key: "assessment",
    label: "Assessment",
    modules: [
      "assessment_plan",
      "assessment_execution",
      "assessment_review",
      "assessment_result",
    ],
  },
  { key: "risk", label: "Risk", modules: ["poam"] },
  {
    key: "administration",
    label: "Administration",
    modules: [
      "organization",
      "user",
      "role",
      "role_mapping",
      "cmdb",
      "validation_rule",
      "llm_configuration",
    ],
  },
];

export function PermissionMatrix({
  availablePermissions,
  selected,
  onChange,
}: PermissionMatrixProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const togglePerm = (code: string) => {
    const next = new Set(selected);
    const perm = availablePermissions.find((p) => p.code === code);

    if (!perm) return;

    if (next.has(code)) {
      // Unchecking a permission
      next.delete(code);

      // If unchecking view, also uncheck dependent permissions (create, update, delete, export, approve, publish)
      if (perm.action === "view") {
        const dependentActions = [
          "create",
          "update",
          "delete",
          "export",
          "approve",
          "publish",
        ];
        availablePermissions
          .filter(
            (p) =>
              p.module === perm.module && dependentActions.includes(p.action),
          )
          .forEach((p) => next.delete(p.code));
      }
    } else {
      // Checking a permission
      next.add(code);
    }

    onChange(next);
  };

  const toggleModule = (module: string) => {
    const moduleConfig = MODULE_PERMISSIONS_CONFIG[module];
    const codes = availablePermissions
      .filter(
        (p) => p.module === module && (moduleConfig?.has(p.action) ?? true),
      )
      .map((p) => p.code);
    const allSel = codes.every((c) => selected.has(c));
    const next = new Set(selected);
    codes.forEach((c) => (allSel ? next.delete(c) : next.add(c)));
    onChange(next);
  };

  const toggleGroupModules = (groupModules: string[]) => {
    const codes = availablePermissions
      .filter((p) => {
        const moduleConfig = MODULE_PERMISSIONS_CONFIG[p.module];
        return (
          groupModules.includes(p.module) &&
          (moduleConfig?.has(p.action) ?? true)
        );
      })
      .map((p) => p.code);
    const allSel = codes.length > 0 && codes.every((c) => selected.has(c));
    const next = new Set(selected);
    codes.forEach((c) => (allSel ? next.delete(c) : next.add(c)));
    onChange(next);
  };

  const toggleAction = (action: string) => {
    const codes = availablePermissions
      .filter((p) => {
        const moduleConfig = MODULE_PERMISSIONS_CONFIG[p.module];
        return p.action === action && (moduleConfig?.has(p.action) ?? true);
      })
      .map((p) => p.code);
    const allSel = codes.every((c) => selected.has(c));
    const next = new Set(selected);
    codes.forEach((c) => (allSel ? next.delete(c) : next.add(c)));
    onChange(next);
  };

  // Modules hidden from the roles permission matrix in the FE (data untouched).
  const HIDDEN_MODULES = new Set(["report_configuration"]);
  const visiblePermissions = availablePermissions.filter(
    (p) => !HIDDEN_MODULES.has(p.module),
  );

  const permsByModule = visiblePermissions.reduce<
    Record<string, AvailablePermission[]>
  >((acc, p) => {
    (acc[p.module] ??= []).push(p);
    return acc;
  }, {});

  // Modules from API that aren't in any defined group → bucket as "Other"
  const knownModules = new Set(MODULE_GROUPS.flatMap((g) => g.modules));
  const ungrouped = Object.keys(permsByModule).filter(
    (m) => !knownModules.has(m),
  );
  const groupsToRender: ModuleGroup[] =
    ungrouped.length > 0
      ? [
          ...MODULE_GROUPS,
          { key: "insights", label: "Insights", modules: ungrouped },
        ]
      : MODULE_GROUPS;

  // Build action column list from data, ordered by ACTION_ORDER preference
  const uniqueActions = new Set(visiblePermissions.map((p) => p.action));
  const allActions = [
    ...ACTION_ORDER.filter((a) => uniqueActions.has(a)),
    ...[...uniqueActions].filter((a) => !ACTION_ORDER.includes(a)),
  ];

  return (
    <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-30 bg-[#f8fafc] dark:bg-[#1a1a2e]">
          <tr>
            <th className="text-left text-[11px] font-bold d tracking-wider px-3 py-2.5 border-b border-border sticky left-0 top-0 bg-[#f8fafc] dark:bg-[#1a1a2e] z-40 min-w-[200px]">
              Module
            </th>
            {allActions.map((a) => {
              const codes = availablePermissions
                .filter((p) => {
                  const moduleConfig = MODULE_PERMISSIONS_CONFIG[p.module];
                  return (
                    p.action === a && (moduleConfig?.has(p.action) ?? true)
                  );
                })
                .map((p) => p.code);
              const allSel =
                codes.length > 0 && codes.every((c) => selected.has(c));
              return (
                <th
                  key={a}
                  className="text-center text-[11px] font-bold d tracking-wider px-3 py-2.5 border-b border-border whitespace-nowrap w-20 bg-[#f8fafc] dark:bg-[#1a1a2e]"
                >
                  <button
                    onClick={() => toggleAction(a)}
                    className="hover:text-foreground transition-colors"
                    title={`Toggle ${a} for all modules`}
                  >
                    {ACTION_LABELS[a] ?? a}
                    {allSel && <span className="ml-1 text-indigo-500">●</span>}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {groupsToRender.map((group) => {
            const modulesInGroup = group.modules.filter(
              (m) => permsByModule[m],
            );
            if (modulesInGroup.length === 0) return null;
            const isCollapsed = collapsedGroups.has(group.key);
            const groupCodes = availablePermissions
              .filter((p) => {
                const moduleConfig = MODULE_PERMISSIONS_CONFIG[p.module];
                return (
                  modulesInGroup.includes(p.module) &&
                  (moduleConfig?.has(p.action) ?? true)
                );
              })
              .map((p) => p.code);
            const groupSelCount = groupCodes.filter((c) =>
              selected.has(c),
            ).length;
            const allGroupSel =
              groupCodes.length > 0 && groupSelCount === groupCodes.length;
            const someGroupSel =
              groupSelCount > 0 && groupSelCount < groupCodes.length;

            return (
              <Fragment key={group.key}>
                {/* Group header row */}
                <tr className="bg-muted/40 dark:bg-white/[0.03] border-b border-border">
                  <td
                    colSpan={allActions.length + 1}
                    className="px-3 py-2 sticky left-0 z-20 bg-muted/40 dark:bg-white/[0.03] border-r border-border/50"
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className="flex items-center gap-1 d hover:text-foreground transition-colors"
                        title={isCollapsed ? "Expand" : "Collapse"}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="size-3.5" />
                        ) : (
                          <ChevronDown className="size-3.5" />
                        )}
                      </button>
                      <input
                        type="checkbox"
                        checked={allGroupSel}
                        ref={(el) => {
                          if (el) el.indeterminate = someGroupSel;
                        }}
                        onChange={() => toggleGroupModules(modulesInGroup)}
                        className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer"
                      />
                      <span className="text-[11px] font-bold tracking-wider">
                        {group.label}
                      </span>
                      <span className="text-[11px] d font-medium">
                        {groupSelCount} / {groupCodes.length}
                      </span>
                    </div>
                  </td>
                </tr>

                {/* Module rows */}
                {!isCollapsed &&
                  modulesInGroup.map((mod, idx) => {
                    const perms = permsByModule[mod];
                    const moduleConfig = MODULE_PERMISSIONS_CONFIG[mod];
                    const codes = perms
                      .filter((p) => moduleConfig?.has(p.action) ?? true)
                      .map((p) => p.code);
                    const allSel =
                      codes.length > 0 && codes.every((c) => selected.has(c));
                    const someSel = codes.some((c) => selected.has(c));
                    return (
                      <tr
                        key={mod}
                        className={`hover:bg-accent/30 transition-colors ${idx % 2 ? "bg-muted/10 dark:bg-white/[0.01]" : ""}`}
                      >
                        <td className="px-3 py-2 sticky left-0 bg-card z-10 border-r border-border/50">
                          <label className="flex items-center gap-2.5 cursor-pointer whitespace-nowrap pl-6">
                            <input
                              type="checkbox"
                              checked={allSel}
                              ref={(el) => {
                                if (el) el.indeterminate = someSel && !allSel;
                              }}
                              onChange={() => toggleModule(mod)}
                              className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer"
                            />
                            <span className="font-medium">
                              {MODULE_LABELS[mod] ?? mod}
                            </span>
                          </label>
                        </td>
                        {allActions.map((action) => {
                          const perm = perms.find((p) => p.action === action);

                          // Check if action is supported for this module based on config
                          const moduleConfig = MODULE_PERMISSIONS_CONFIG[mod];
                          const isConfiguredToSupport =
                            moduleConfig?.has(action) ?? true;

                          // Check if action is not supported for this module
                          const isNotSupported =
                            !perm || !isConfiguredToSupport;

                          if (isNotSupported)
                            return (
                              <td
                                key={action}
                                className="text-center px-3 py-2 w-20"
                              >
                                <span className="d/30" title="Not applicable">
                                  —
                                </span>
                              </td>
                            );

                          // Check if view permission is selected for this module
                          const viewPerm = perms.find(
                            (p) => p.action === "view",
                          );
                          const hasViewPermission = viewPerm
                            ? selected.has(viewPerm.code)
                            : false;

                          // Actions that require view permission
                          const requiresViewPermission = [
                            "create",
                            "update",
                            "delete",
                            "export",
                            "approve",
                            "publish",
                          ].includes(action);
                          const isDisabledByView =
                            requiresViewPermission && !hasViewPermission;

                          // Modules that don't support import/export
                          const noImportExportModules = [
                            "assessment_result",
                            "assessment_execution",
                            "assessment_review",
                          ];
                          const isImportExportAction = [
                            "import",
                            "export",
                          ].includes(action);
                          const isDisabledPermanently =
                            isImportExportAction &&
                            noImportExportModules.includes(mod);

                          const isDisabled =
                            isDisabledByView || isDisabledPermanently;
                          const disabledReason = isDisabledPermanently
                            ? `${action} not supported for this module`
                            : isDisabledByView
                              ? `View permission required to enable ${action}`
                              : perm.displayName;

                          return (
                            <td
                              key={action}
                              className="text-center px-3 py-2 w-20"
                            >
                              <label
                                className={`inline-flex items-center justify-center ${isDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}
                                title={disabledReason}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected.has(perm.code)}
                                  onChange={() => togglePerm(perm.code)}
                                  disabled={isDisabled}
                                  className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer disabled:cursor-not-allowed"
                                />
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
